<template>
  <div class="member-form tongxin-layout">
    <h1 class="title-with-underline">
      {{ isEditMode ? '家庭成員資料' : '家庭成員資料設定' }}
    </h1>

    <form @submit.prevent="handleSubmit">
      <!-- 姓名 -->
      <div class="form-group">
        <label class="required">姓名</label>
        <input
          v-model="formData.name"
          @input="handleNameInput"
          type="text"
          class="form-control"
          placeholder="請輸入姓名"
          maxlength="50"
          required
        />
        <div class="error-message" v-if="errors.name">{{ errors.name }}</div>
      </div>

      <!-- 身分證字號 -->
      <div class="form-group">
        <label class="required">身分證字號</label>
        <input
          v-model="formData.idno"
          type="text"
          class="form-control"
          placeholder="請輸入身分證字號"
          maxlength="10"
          required
        />
        <div class="error-message" v-if="errors.idno">{{ errors.idno }}</div>
      </div>

      <!-- 出生年月日 -->
      <div class="form-group">
        <label class="required">出生年月日</label>
        <input
          v-model="formData.birthday"
          type="date"
          class="form-control"
          required
        />
        <div class="error-message" v-if="errors.birthday">{{ errors.birthday }}</div>
      </div>

      <!-- 與本人關係 -->
      <div class="form-group">
        <label class="required">與本人關係</label>
        <select v-model="formData.relationship" class="form-control" required>
          <option value="">請選擇</option>
          <option value="配偶">配偶</option>
          <option value="父親">父親</option>
          <option value="母親">母親</option>
          <option value="子女">子女</option>
          <option value="兄弟姊妹">兄弟姊妹</option>
          <option value="其他">其他</option>
        </select>
        <div class="error-message" v-if="errors.relationship">{{ errors.relationship }}</div>
      </div>

      <!-- 提示訊息 -->
      <div class="reminder-green" v-if="!isEditMode">
        我們將於送出申請後，發送「家庭成員授權通知」簡訊給您的親友，待對方同意後即可使用此資料代為預約。
      </div>

      <div class="reminder-green" v-if="isEditMode">
        授權 王大明先生（或 xxx 女士）已授權您新增本項修改紀錄，並代替本人處理相關事務。
      </div>

      <!-- 按鈕 -->
      <button
        type="submit"
        class="btn btn-primary btn-block mb-3"
        :disabled="isLoading"
      >
        {{ isEditMode ? '儲存更新' : '新增' }}
      </button>

      <button
        v-if="isEditMode"
        type="button"
        class="btn btn-outline-danger btn-block mb-3"
        @click="handleDelete"
        :disabled="isLoading"
      >
        刪除
      </button>
    </form>

    <div style="padding-bottom: 96px"></div>
    <div class="fixed-footer-navigation">
      <TongxinNavigation
        :backButtonIsVisible="true"
        :nextButtonIsVisible="false"
        @back="goBack"
      />
    </div>

    <!-- Loading overlay -->
    <div v-if="isLoading" class="loading-overlay">
      <b-spinner style="border-width: 2px" />
    </div>
  </div>
</template>

<script>
import TongxinNavigation from '@/components/web/proj/tongxin/layout/TongxinNavigation.vue'
import familyManager from '@/mixins/web/tongxin/familyManager'

export default {
  name: 'TongxinFamilyMemberForm',
  mixins: [familyManager],
  components: {
    TongxinNavigation,
  },
  props: {
    memberId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      formData: {
        name: '',
        idno: '',
        birthday: '',
        relationship: '',
      },
      errors: {},
      currentMember: null,
    }
  },
  computed: {
    isEditMode() {
      return !!this.memberId && this.memberId !== 'new'
    },
    isLoading() {
      return this.isCallingFamilyApi
    },
  },
  created() {
    if (this.isEditMode) {
      this.loadMemberData()
    }
  },
  methods: {
    validateName(name) {
      const trimmedName = (name || '').trim()
      if (!trimmedName) {
        return ''
      }

      const namePattern = /^(?=.{1,50}$)[\p{L}]+(?:[ 　][\p{L}]+)*$/u
      if (!namePattern.test(trimmedName)) {
        return '不可包含特殊符號、數字，且長度不可超過 50 個字元'
      }

      return ''
    },

    handleNameInput() {
      const nameError = this.validateName(this.formData.name)
      if (nameError) {
        this.$set(this.errors, 'name', nameError)
        return
      }
      if (this.errors.name) {
        this.$delete(this.errors, 'name')
      }
    },

    loadMemberData() {
      // 從 query 讀取成員資料
      const dataStr = this.$route.query.data
      if (dataStr) {
        try {
          this.currentMember = JSON.parse(dataStr)
          this.formData = {
            name: this.currentMember.ptName || '',
            idno: this.currentMember.ptIdno || '',
            birthday: this.formatBirthday(this.currentMember.ptBirthday),
            relationship: this.currentMember.relationship || '',
          }
        } catch (error) {
          console.error('解析成員資料失敗:', error)
        }
      }
    },

    validateForm() {
      this.errors = {}
      let isValid = true

      if (!this.formData.name || !this.formData.name.trim()) {
        this.errors.name = '請輸入姓名'
        isValid = false
      } else {
        const nameError = this.validateName(this.formData.name)
        if (nameError) {
          this.errors.name = nameError
          isValid = false
        }
      }

      if (!this.formData.idno || !this.formData.idno.trim()) {
        this.errors.idno = '請輸入身分證字號'
        isValid = false
      } else if (!/^[A-Z][A-Z0-9]\d{8}$/.test(this.formData.idno.trim())) {
        this.errors.idno = '身分證字號或居留證號格式不正確'
        isValid = false
      }

      if (!this.formData.birthday) {
        this.errors.birthday = '請選擇出生年月日'
        isValid = false
      }

      if (!this.formData.relationship) {
        this.errors.relationship = '請選擇與本人關係'
        isValid = false
      }

      return isValid
    },

    async handleSubmit() {
      if (!this.validateForm()) {
        return
      }

      try {
        if (this.isEditMode) {
          await this.handleUpdateRelation(this.currentMember.rowid || this.memberId, this.formData)
        } else {
          await this.handleAddRelation(this.formData)
        }
      } catch (error) {
        console.error('表單提交失敗:', error)
      }
    },

    async handleDelete() {
      if (!this.currentMember) {
        return
      }

      const result = await this.$swal({
        title: '確定要刪除此親友資料？',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: '確定刪除',
        cancelButtonText: '取消',
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
      })

      if (!result.value) {
        return
      }

      try {
        await this.handleDeleteRelation(this.currentMember.rowid || this.memberId, this.currentMember.ptIdno || this.formData.idno)
      } catch (error) {
        console.error('刪除失敗:', error)
      }
    },

    /**
     * 將 SAW API 生日格式轉換為 date input 格式
     * "1990/5/15 上午 12:00:00" → "1990-05-15"
     */
    formatBirthday(birthday) {
      if (!birthday) return ''
      // 已經是 YYYY-MM-DD 格式
      if (/^\d{4}-\d{2}-\d{2}$/.test(birthday)) return birthday
      // SAW API 格式：取日期部分，轉換斜線為橫線
      const datePart = birthday.split(' ')[0]
      if (!datePart) return ''
      const parts = datePart.split('/')
      if (parts.length !== 3) return ''
      const year = parts[0]
      const month = parts[1].padStart(2, '0')
      const day = parts[2].padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    goBack() {
      this.$router.back()
    },
  },
}
</script>

<style lang="scss" scoped>
.member-form {
  form {
    margin-top: 40px;

    .form-group {
      margin-bottom: 24px;

      label {
        display: block;
        font-size: 16px;
        font-weight: 600;
        color: #333;
        margin-bottom: 8px;

        &.required::after {
          content: ' *';
          color: #dc3545;
        }
      }

      .form-control {
        width: 100%;
        padding: 6px 12px;
        font-size: 16px;
        border: 2px solid #e0e0e0;
        border-radius: 8px;
        transition: border-color 0.3s;

        &:focus {
          outline: none;
          border-color: #207868;
        }

        &::placeholder {
          color: #999;
        }
      }

      select.form-control {
        cursor: pointer;
      }

      .error-message {
        color: #dc3545;
        font-size: 14px;
        margin-top: 4px;
      }
    }

    .reminder-green {
      background: #e8f5f3;
      border-left: 4px solid #207868;
      padding: 16px;
      margin-bottom: 24px;
      border-radius: 4px;
      font-size: 14px;
      color: #333;
      line-height: 1.6;
    }

    .btn {
      padding: 12px 24px;
      font-size: 16px;
      font-weight: 600;
      border-radius: 8px;
      transition: all 0.3s;

      &.btn-primary {
        background: #207868;
        border: none;
        color: white;

        &:hover:not(:disabled) {
          background: #186052;
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }

      &.btn-secondary {
        background: #6c757d;
        border: none;
        color: white;

        &:hover {
          background: #5a6268;
        }
      }

      &.btn-outline-danger {
        background: white;
        border: 2px solid #dc3545;
        color: #dc3545;

        &:hover:not(:disabled) {
          background: #dc3545;
          color: white;
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }
    }
  }
}
</style>
